import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPreloadedSimCheckoutNavigateState,
  getPreloadedSimPostDeviceUpsellPath,
  preparePreloadedSimSkipAllUpsellsForCheckout,
  resolvePreloadedSimUpsellStepSkips,
} from "../utils/preloaded-sim-upsell-step-skips";
import { getActivationFlowData } from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import { usePreloadedSimChooseServicePlan } from "../hooks/use-preloaded-sim-choose-service-plan";
import { Label } from "app/components/ui/label";
import { Button } from "app/components/ui/button";
import { Skeleton } from "app/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "app/components/ui/select";

/**
 * Upsell card only. Maps API-built lines like `Primary Data: {N}GB on AT&T's 5G network`
 * → `Primary Data: {N}GB/mo on AT&T's network` for any allotment (any digits), plus Unlimited/TB.
 */
function formatUpsellPrimaryDataFeatureLine(feature: string): string {
  const trimmed = feature.trim();
  if (!/^primary\s+data:/i.test(trimmed)) return feature;

  let s = feature
    .replace(
      /\s+on\s+AT&T['\u2019]s\s+5G(?:\s+nationwide)?\s+network/gi,
      " on AT&T's network",
    )
    .replace(/\s+on\s+AT&T\s+5G\s+network/gi, " on AT&T's network");

  const alreadyHasPerMo =
    /\b(?:gb|tb)\s*\/mo\b/i.test(s) || /\bunlimited\s*\/mo\b/i.test(s);
  if (!alreadyHasPerMo) {
    s = s.replace(
      /(primary\s+data:\s*)((?:\d+(?:\.\d+)?)\s*(?:gb|tb))(\s+on\s+)/i,
      "$1$2/mo$3",
    );
    s = s.replace(/(primary\s+data:\s*)(unlimited)(\s+on\s+)/i, "$1$2/mo$3");
  }

  return s;
}

function FeatureArrow() {
  return (
    <span className="flex-shrink-0 mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem] text-[#1EC677]">
      <svg
        className="w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 12H22M22 12L15 7M22 12L15 17"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export const ChooseServicePlan = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const skips = resolvePreloadedSimUpsellStepSkips(
      getActivationFlowData()?.simData,
    );
    if (!skips.skipChooseServicePlan) return;
    const next = getPreloadedSimPostDeviceUpsellPath(skips);
    if (next) {
      navigate(next, { replace: true });
      return;
    }
    preparePreloadedSimSkipAllUpsellsForCheckout();
    navigate("/checkout", getPreloadedSimCheckoutNavigateState());
  }, [navigate]);

  const {
    // INCLUDED_PRIMARY_DATA_FEATURES,
    includedPlan,
    includedFrequencyKey,
    primaryDataTierKey,
    setPrimaryDataTierKey,
    serviceFrequencyKey,
    setServiceFrequencyKey,
    planSelectOptions,
    showPlanSelect,
    frequencyOptions,
    showFrequencySelect,
    showPlanSelectSkeleton,
    showFrequencySelectSkeleton,
    billingSuffixForUpgradeTitle,
    frequencyLabelMap,
    canLockInSelection,
    selectedUpgradePlan,
    includedPrice,
    includedPriceNumber,
    includedOriginalPrice,
    includedFeatures,
    dataAndFrequencyUpsellFeatures,
    isSkeleton,
    showEmptyPlans,
    isError,
    error,
    refetch,
    goKeepIncludedAllocation,
    handleUpsellSelectOpenChange,
    goLockInDataAndFrequencyUpsell,
    showUpgradeToCard,
    computeOriginalPriceCompact,
    formatUsdCompact,
    frequencyDisplayLabel,
    frequencySuffixFromKey,
    parseCurrencyToNumber,
    upgradeCardTitle,
  } = usePreloadedSimChooseServicePlan();

  const [openUpsellSelect, setOpenUpsellSelect] = useState<
    "primary" | "frequency" | null
  >(null);

  const onPrimaryUpsellSelectOpenChange = useCallback(
    (open: boolean) => {
      handleUpsellSelectOpenChange(open);
      if (open) {
        setOpenUpsellSelect("primary");
      } else {
        setOpenUpsellSelect((prev) => (prev === "primary" ? null : prev));
      }
    },
    [handleUpsellSelectOpenChange],
  );

  const onFrequencyUpsellSelectOpenChange = useCallback(
    (open: boolean) => {
      handleUpsellSelectOpenChange(open);
      if (open) {
        setOpenUpsellSelect("frequency");
      } else {
        setOpenUpsellSelect((prev) => (prev === "frequency" ? null : prev));
      }
    },
    [handleUpsellSelectOpenChange],
  );

  if (isError) {
    return (
      <div className="px-5 md:px-6 xl:px-7 2xl:pr-[1.875rem] py-4 sm:py-6 md:py-8 lg:py-10 h-[calc(100vh-13.75rem)]">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-5 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
            Failed to load Upsell pricing plans
          </h3>
          <p className="max-w-sm text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 sm:mb-4 lg:mb-5 mx-auto">
            {(error as any)?.data?.message ||
              "We couldn't retrieve the plans at this moment. Please try again."}
          </p>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (showEmptyPlans) {
    return (
      <div className="px-5 md:px-6 xl:px-7 2xl:pr-[1.875rem] py-4 sm:py-6 md:py-8 lg:py-10 h-[calc(100vh-13.75rem)]">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-5 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
            No service plans to show right now
          </h3>
          <p className="max-w-md text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 sm:mb-4 lg:mb-5 mx-auto">
            We didn&apos;t receive any plan options from our catalog. This may
            be temporary—try refreshing. If it keeps happening, contact support
            and we&apos;ll help you finish activation.
          </p>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="text-center mb-4 sm:mb-[1.125rem] lg:mb-5">
        <h2 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2vw+1rem),2.25rem)] font-normal leading-snug md:leading-normal lg:leading-[3.3125rem] mb-1 sm:mb-1.5 lg:mb-2">
          Upgrade Service Plan &amp; Save Big
        </h2>
        <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base lg:text-lg font-normal leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7">
          Increase your data allotment or extend your service duration.
        </p>
      </div>

      <ul className="flex flex-wrap [@media(max-width:928px)]:flex-wrap-reverse gap-[1.125rem] justify-center w-full sm:gap-5 md:gap-6 xl:gap-8 2xl:gap-[2.375rem]">
        {(showUpgradeToCard ? [0, 1] : [0]).map((index) => (
          <li
            key={index}
            className="flex overflow-hidden relative flex-col w-full sm:w-[25.75rem] max-w-[25.75rem] rounded-2xl md:rounded-[1.25rem]"
          >
            {index === 1 ? (
              <div className="absolute top-0 left-0 bg-[#1D1D1B] w-full h-11 sm:h-[3.15rem] lg:h-[3.9375rem] rounded-t-[1.25rem] md:rounded-t-3xl" />
            ) : null}
            <div
              className={`relative z-[2] flex justify-center items-center text-center ${
                index === 2
                  ? "[@media(min-width:1392px)]:h-10"
                  : index === 0
                  ? "[@media(min-width:928px)]:h-9 lg:h-10"
                  : "h-8 sm:h-9 lg:h-10"
              }`}
            >
              {index === 1 && isSkeleton ? (
                <>
                  <Skeleton className="w-16 sm:w-[4.375rem] lg:w-[4.75rem] h-3.5 sm:h-4 lg:h-[1.125rem] bg-white/20 rounded-md" />
                  <Skeleton className="ml-1 sm:ml-1.5 w-[1.1875rem] h-[0.825rem] sm:w-[1.3063rem] sm:h-[0.925rem] lg:w-[1.425rem] lg:h-4 bg-white/20 rounded" />
                </>
              ) : null}
              {index === 1 && !isSkeleton ? (
                <>
                  <span className="text-white text-xs sm:text-[0.825rem] lg:text-sm font-medium leading-[0.875rem] sm:leading-4 lg:leading-[1.125rem]">
                    Best Value
                  </span>
                  <img
                    src="/assets/crown.webp"
                    alt="Sparkles"
                    className="ml-1 -mt-[0.1875rem] sm:-mt-1 sm:ml-1.5 w-[1.1875rem] h-[0.6875rem] sm:w-[1.3063rem] sm:h-[0.7563rem] lg:w-[1.425rem] lg:h-[0.825rem]"
                  />
                </>
              ) : null}
            </div>
            <div
              className={`relative flex-1 flex flex-col justify-between bg-white w-full p-5 sm:p-6 md:px-[1.625rem] md:py-7 lg:py-8 rounded-2xl md:rounded-[1.25rem] ${
                index === 1
                  ? "border-x-2 border-b-2 border-dashed border-[#1D1D1B]"
                  : "border border-[#EBEBEB]"
              }`}
            >
              {index === 1 ? (
                <div className="absolute z-0 top-0 inset-x-0 h-3 sm:h-3.5 lg:h-4 bg-white rounded-t-2xl md:rounded-t-[1.25rem]" />
              ) : null}
              <div className="flex-1 flex flex-col">
                {(() => {
                  const isIncludedCard = index === 0;
                  const oldPrice = isIncludedCard
                    ? includedOriginalPrice
                    : computeOriginalPriceCompact(
                        selectedUpgradePlan?.price,
                        selectedUpgradePlan?.savings,
                      );
                  const upgradeTotal =
                    parseCurrencyToNumber(selectedUpgradePlan?.price) ?? 0;
                  // const savingsNum =
                  //   parseCurrencyToNumber(selectedUpgradePlan?.savings) ?? 0;

                  const newPrice = isIncludedCard
                    ? includedPrice
                    : (() => {
                        if (upgradeTotal <= 0) return "$0";
                        const delta = Math.max(
                          0,
                          upgradeTotal - includedPriceNumber,
                        );
                        return formatUsdCompact(delta);
                      })();
                  const priceSuffix = isIncludedCard
                    ? `/${frequencySuffixFromKey(includedFrequencyKey)}`
                    : `/${frequencySuffixFromKey(serviceFrequencyKey)}`;

                  const includedPlanName =
                    typeof includedPlan?.name === "string" &&
                    includedPlan.name.trim()
                      ? includedPlan.name.trim()
                      : "Included";

                  const title = isIncludedCard
                    ? `${includedPlanName} Plan ${frequencyDisplayLabel(
                        includedFrequencyKey,
                      )}`
                    : upgradeCardTitle(
                        primaryDataTierKey,
                        billingSuffixForUpgradeTitle,
                      );
                  const description = isIncludedCard
                    ? "Stay connected and protected with AT&T’s 5G nationwide network and Aura’s all-in-one security plan."
                    : "Maximize your benefits and savings with more high-speed data and extended service terms.";

                  const renewsFeature = dataAndFrequencyUpsellFeatures.find(
                    (f) => f.toLowerCase().includes("renews"),
                  );
                  // const renewsText = renewsFeature
                  //   ? renewsFeature.split("(")[0].trim()
                  //   : null;
                  const renewsText = renewsFeature ? (
                    renewsFeature.includes("(") ? (
                      <>
                        {renewsFeature
                          .substring(0, renewsFeature.indexOf("("))
                          .trim()}
                        <br />
                        <span className="text-[0.5625rem] sm:text-[0.625rem] lg:text-[0.6875rem] leading-3 sm:leading-[0.8125rem] lg:leading-[0.875rem]">
                          {renewsFeature
                            .substring(renewsFeature.indexOf("("))
                            .trim()}
                        </span>
                      </>
                    ) : (
                      renewsFeature.trim()
                    )
                  ) : null;

                  const featureList = isIncludedCard
                    ? includedFeatures
                    : dataAndFrequencyUpsellFeatures;
                  const featureSkeletonCount = isIncludedCard ? 5 : 6;

                  return (
                    <>
                      <h4
                        className={`text-[#1D2530] font-mobile text-xl sm:text-[1.375rem] lg:text-2xl font-bold leading-6 sm:leading-7 lg:leading-8 mb-1 sm:mb-[0.34rem] ${
                          isIncludedCard ? "whitespace-nowrap" : ""
                        }`}
                      >
                        {isSkeleton ? (
                          isIncludedCard ? (
                            <div className="flex items-center">
                              <Skeleton className="w-[11.5rem] sm:w-[11.75rem] md:w-48 h-6 sm:h-7 lg:h-8 bg-[#E2E4E9] rounded-md mb-1 sm:mb-1.5" />
                              <Skeleton className="inline-block align-middle mt-1 ml-1 sm:ml-1.5 w-14 sm:w-[3.625rem] lg:w-[3.875rem] h-4 sm:h-5 bg-[#E2E4E9] rounded-md" />
                            </div>
                          ) : (
                            <Skeleton className="w-64 sm:w-[17rem] lg:w-[18.75rem] h-6 sm:h-7 lg:h-8 bg-[#E2E4E9] rounded-md mb-1 sm:mb-1.5" />
                          )
                        ) : isIncludedCard ? (
                          <>
                            {title}{" "}
                            <span className="inline-block text-[#999999] font-inter text-[0.825rem] sm:text-sm lg:text-[0.925rem] font-normal leading-[0.875rem] sm:leading-4 lg:leading-[1.125rem] ml-1 sm:ml-1.5">
                              Included
                            </span>
                          </>
                        ) : (
                          title
                        )}
                      </h4>

                      {isSkeleton ? (
                        isIncludedCard ? (
                          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-2.5 lg:mb-[0.725rem]">
                            <div className="flex gap-1 sm:gap-1.5 items-end">
                              <Skeleton className="w-[7.25rem] sm:w-[7.5rem] lg:w-[7.75rem] h-[2.125rem] sm:h-9 lg:h-10 bg-[#E2E4E9] rounded-md" />
                              <Skeleton className="w-5 sm:w-6 lg:w-7 h-5 sm:h-6 lg:h-7 bg-[#E2E4E9] rounded-md pb-1" />
                            </div>
                          </div>
                        ) : (
                          <div className="bg-[#FFF5F2] border border-[#FFE8E0] p-2.5 sm:p-3 lg:p-3.5 rounded-lg md:rounded-xl w-full">
                            <div className="flex justify-between items-center gap-2 mb-1 sm:mb-1.5">
                              <Skeleton className="h-[1.125rem] sm:h-5 lg:h-[1.375rem] w-[7.875rem] sm:w-[8.25rem] lg:w-[8.5rem] bg-[#E2E4E9] rounded" />
                              <Skeleton className="h-[1.125rem] sm:h-5 lg:h-[1.375rem] w-[2.75rem] sm:w-[3.125rem] lg:w-[3.375rem] bg-[#E2E4E9] rounded" />
                            </div>
                            <div className="flex justify-between items-center gap-2 mb-2 sm:mb-2.5">
                              <Skeleton className="h-[1.125rem] sm:h-5 lg:h-[1.375rem] w-[9.375rem] sm:w-40 lg:w-[10.625rem] bg-[#E2E4E9] rounded" />
                              <Skeleton className="h-[1.125rem] sm:h-5 lg:h-[1.375rem] w-[3.375rem] sm:w-[3.625rem] lg:w-16 bg-[#E2E4E9] rounded" />
                            </div>
                            <Skeleton className="h-[0.875rem] sm:h-4 lg:h-[1.125rem] w-[6.75rem] sm:w-[7.25rem] lg:w-[8.25rem] mb-0.5 sm:mb-1 bg-[#E2E4E9] rounded" />
                            <div className="flex justify-between items-end gap-2">
                              <Skeleton className="h-8 sm:h-9 lg:h-10 w-[3.375rem] sm:w-[3.875rem] lg:w-[4.25rem] bg-[#E2E4E9] rounded-md" />
                              <Skeleton
                                className={`${
                                  serviceFrequencyKey !== "monthly"
                                    ? "h-9 sm:h-10 lg:h-[2.875rem]"
                                    : "h-[1.375rem] sm:h-7 lg:h-[1.875rem]"
                                } w-[6.875rem] sm:w-32 lg:w-[9.25rem] bg-[#E2E4E9] rounded border border-[#E5E7EB]`}
                              />
                            </div>
                          </div>
                        )
                      ) : isIncludedCard ? (
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 lg:gap-2.5 mb-2 sm:mb-2.5 lg:mb-[0.725rem]">
                          {oldPrice ? (
                            <p className="font-mobile text-[#98A2B3] text-[2rem] sm:text-[2.125rem] lg:text-4xl font-normal leading-[2.125rem] sm:leading-9 lg:leading-10 line-through">
                              {oldPrice}
                            </p>
                          ) : null}
                          <div className="flex flex-wrap items-end gap-1 sm:gap-1.5">
                            <p className="font-mobile text-[#1D2530] text-[2rem] sm:text-[2.125rem] lg:text-4xl font-bold leading-[2.125rem] sm:leading-9 lg:leading-10">
                              {newPrice}
                            </p>
                            <span className="inline-block mt-auto pb-[0.1875rem] sm:pb-0.5 text-[#5C6F8A] text-[0.925rem] sm:text-base lg:text-lg font-normal leading-5 sm:leading-6 lg:leading-7">
                              {priceSuffix}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-[#FFF5F2] border border-[#FFE8E0] p-2.5 sm:p-3 lg:p-3.5 rounded-lg md:rounded-xl w-full">
                          {/* {oldPrice && savingsNum > 0 ? (
                            <>
                              <div className="flex justify-between items-center gap-2 mb-1 sm:mb-1.5">
                                <span className="inline-block text-sm sm:text-[0.925rem] lg:text-base text-[#4B5563] font-medium leading-5 sm:leading-[1.375rem] lg:leading-6">
                                  Actual Plan Price
                                </span>
                                <span className="inline-block text-sm sm:text-[0.925rem] lg:text-base text-[#111827] font-semibold leading-5 sm:leading-[1.375rem] lg:leading-6 line-through">
                                  {oldPrice}
                                </span>
                              </div>
                              <div className="flex justify-between items-center gap-3 mb-1.5 sm:mb-2">
                                <span className="inline-block text-sm sm:text-[0.925rem] lg:text-base text-[#111827] font-medium leading-5 sm:leading-[1.375rem] lg:leading-6">
                                  Discount
                                </span>
                                <span className="inline-block text-sm sm:text-[0.925rem] lg:text-base text-[#111827] font-semibold leading-5 sm:leading-[1.375rem] lg:leading-6">
                                  -{formatUsdCompact(savingsNum)}
                                </span>
                              </div>
                            </>
                          ) : null} */}

                          <div className="flex justify-between items-center gap-2 mb-1 sm:mb-1.5 text-[#475467]">
                            <span className="inline-block text-[0.825rem] sm:text-sm lg:text-[0.925rem] text-[#4B5563] font-medium leading-[1.125rem] sm:leading-5 lg:leading-[1.375rem]">
                              Original Plan Price
                            </span>
                            <span className="inline-block text-[0.825rem] sm:text-sm lg:text-[0.925rem] text-[#111827] font-semibold leading-[1.125rem] sm:leading-5 lg:leading-[1.375rem] line-through">
                              {formatUsdCompact(upgradeTotal)}
                            </span>
                          </div>

                          <div className="flex justify-between items-center gap-2 mb-2 sm:mb-2.5">
                            <span className="inline-block text-[0.825rem] sm:text-sm lg:text-[0.925rem] text-[#E54D15] font-medium leading-[1.125rem] sm:leading-5 lg:leading-[1.375rem]">
                              Credit from {includedPlanName} Plan
                            </span>
                            <span className="inline-block text-[0.825rem] sm:text-sm lg:text-[0.925rem] text-[#E54D15] font-semibold leading-[1.125rem] sm:leading-5 lg:leading-[1.375rem]">
                              -{formatUsdCompact(includedPriceNumber)}
                            </span>
                          </div>

                          <p className="text-[0.6875rem] sm:text-xs lg:text-[0.8125rem] text-[#4B5563] font-medium leading-[0.875rem] sm:leading-4 lg:leading-[1.125rem] mb-0.5 sm:mb-1">
                            Due Today to Upgrade
                          </p>

                          <div className="flex justify-between items-end gap-2">
                            <p className="font-mobile text-[#E54D15] text-[2rem] sm:text-[2.25rem] lg:text-[2.5rem] font-bold leading-8 sm:leading-9 lg:leading-10">
                              {newPrice}
                            </p>
                            {renewsText ? (
                              <span className="inline-block border border-[#E5E7EB] rounded px-1.5 sm:px-2 py-1 sm:py-1.5 text-[0.6875rem] sm:text-xs lg:text-[0.8125rem] text-[#6B7280] bg-white font-normal leading-[0.875rem] sm:leading-4 lg:leading-[1.125rem]">
                                {renewsText}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      )}

                      <div className="text-[#141414] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.125rem] sm:leading-5 lg:leading-6 my-3.5 sm:my-4 lg:my-5">
                        {isSkeleton ? (
                          <div className="flex flex-col gap-1 sm:gap-1.5">
                            <Skeleton className="w-full h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md" />
                            <Skeleton
                              className={
                                isIncludedCard
                                  ? "w-full h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md"
                                  : "w-2/3 h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md"
                              }
                            />
                            {isIncludedCard ? (
                              <Skeleton className="w-1/2 h-5 sm:h-[1.375rem] lg:h-6 bg-[#E2E4E9] rounded-md" />
                            ) : null}
                          </div>
                        ) : (
                          description
                        )}
                      </div>

                      <ul className="space-y-1.5 sm:space-y-2 lg:space-y-2.5 mb-3.5 sm:mb-4 lg:mb-5">
                        {isSkeleton
                          ? Array.from({ length: featureSkeletonCount }).map(
                              (_, i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                >
                                  <Skeleton className="shrink-0 w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5 bg-[#E2E4E9] rounded mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem]" />
                                  <Skeleton className="w-[17.5rem] sm:w-[17.75rem] md:w-72 h-[1.375rem] sm:h-6 bg-[#E2E4E9] rounded-md" />
                                </li>
                              ),
                            )
                          : featureList.map((feature) => {
                              const displayFeature = !isIncludedCard
                                ? formatUpsellPrimaryDataFeatureLine(feature)
                                : feature;
                              return (
                                <li
                                  key={feature}
                                  className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                >
                                  <FeatureArrow />
                                  <p
                                    className={`text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base leading-5 sm:leading-6 md:leading-relaxed ${
                                      !isIncludedCard &&
                                      feature.toLowerCase().includes("renews")
                                        ? "font-medium"
                                        : "font-normal"
                                    }`}
                                  >
                                    {!isIncludedCard &&
                                    feature.toLowerCase().includes("renews") &&
                                    feature.includes("(") ? (
                                      <>
                                        {feature.split("(")[0]}
                                        <span className="text-[#98A3B7] font-normal text-[0.72rem] sm:text-xs lg:text-[0.78125rem]">
                                          ({feature.split("(")[1]}
                                        </span>
                                      </>
                                    ) : (
                                      displayFeature
                                    )}
                                  </p>
                                </li>
                              );
                            })}
                      </ul>

                      {!isIncludedCard ? (
                        isSkeleton ? (
                          <div className="space-y-3.5 sm:space-y-4 md:space-y-[1.125rem] mb-5 sm:mb-6 md:mb-7 w-full">
                            {showPlanSelectSkeleton ? (
                              <div className="flex items-center gap-3">
                                <Skeleton className="w-[3.625rem] sm:w-[3.75rem] md:w-16 h-4 sm:h-5 bg-[#E2E4E9] rounded" />
                                <Skeleton className="flex-1 min-h-6 sm:min-h-[1.625rem] md:min-h-7 bg-[#E2E4E9] rounded" />
                              </div>
                            ) : null}
                            {showFrequencySelectSkeleton ? (
                              <div className="flex items-center gap-3">
                                <Skeleton className="w-[3.625rem] sm:w-[3.75rem] md:w-16 h-4 sm:h-5 bg-[#E2E4E9] rounded" />
                                <Skeleton className="flex-1 min-h-6 sm:min-h-[1.625rem] md:min-h-7 bg-[#E2E4E9] rounded" />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="space-y-3.5 sm:space-y-4 md:space-y-[1.125rem] mb-5 sm:mb-6 md:mb-7">
                            {showPlanSelect ? (
                              <div className="flex items-center gap-3 w-full">
                                <Label
                                  htmlFor="sim-upsell-primary-data"
                                  className="text-[#1D1D1B] text-[0.8125rem] sm:text-[0.84375rem] md:text-sm font-normal w-[3.625rem] sm:w-[3.75rem] md:w-16 whitespace-nowrap mb-0"
                                >
                                  Incl. Data
                                </Label>
                                <Select
                                  open={openUpsellSelect === "primary"}
                                  onOpenChange={onPrimaryUpsellSelectOpenChange}
                                  value={primaryDataTierKey}
                                  onValueChange={setPrimaryDataTierKey}
                                >
                                  <SelectTrigger
                                    id="sim-upsell-primary-data"
                                    className="flex-1 min-h-6 sm:min-h-[1.625rem] md:min-h-7 pr-2 py-1 rounded border-[#B7B6B6] bg-white text-left text-[0.825rem] font-medium text-[#1D1D1B]"
                                  >
                                    <SelectValue placeholder="Select primary data" />
                                    <img
                                      src="/assets/chevron-down.svg"
                                      alt="Chevron Down"
                                      className="size-4 shrink-0"
                                      aria-hidden="true"
                                    />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-60">
                                    <SelectGroup>
                                      {planSelectOptions.map((opt) => (
                                        <SelectItem
                                          key={opt.key}
                                          value={opt.key}
                                          disabled={opt.disabled}
                                          className="text-[0.825rem] sm:text-[0.84375rem] font-normal leading-4 sm:leading-[1.125rem] text-[#1D1D1B]"
                                        >
                                          {opt.label} Primary Data
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : null}
                            {showFrequencySelect ? (
                              <div className="flex items-center gap-3 w-full">
                                <Label
                                  htmlFor="sim-upsell-service-frequency"
                                  className="text-[#1D1D1B] text-[0.8125rem] sm:text-[0.84375rem] md:text-sm font-normal w-[3.625rem] sm:w-[3.75rem] md:w-16 whitespace-nowrap mb-0"
                                >
                                  Pay Every
                                </Label>
                                <Select
                                  open={openUpsellSelect === "frequency"}
                                  onOpenChange={
                                    onFrequencyUpsellSelectOpenChange
                                  }
                                  value={serviceFrequencyKey}
                                  onValueChange={setServiceFrequencyKey}
                                >
                                  <SelectTrigger
                                    id="sim-upsell-service-frequency"
                                    className="flex-1 min-h-6 sm:min-h-[1.625rem] md:min-h-7 pr-2 py-1 rounded border-[#B7B6B6] bg-white text-left text-[0.825rem] font-medium text-[#1D1D1B]"
                                  >
                                    <SelectValue placeholder="Select service frequency" />
                                    <img
                                      src="/assets/chevron-down.svg"
                                      alt="Chevron Down"
                                      className="size-4 shrink-0"
                                      aria-hidden="true"
                                    />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-60">
                                    <SelectGroup>
                                      {frequencyOptions.map((opt) => (
                                        <SelectItem
                                          key={opt.key}
                                          value={opt.key}
                                          disabled={opt.disabled}
                                          className="text-[0.825rem] sm:text-[0.84375rem] font-normal leading-4 sm:leading-[1.125rem] text-[#1D1D1B]"
                                        >
                                          {frequencyLabelMap[opt.key] ||
                                            opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : null}
                          </div>
                        )
                      ) : null}
                    </>
                  );
                })()}
              </div>
              {isSkeleton ? (
                <Skeleton className="w-full h-11 sm:h-[2.875rem] md:h-12 lg:h-14 bg-[#E2E4E9] rounded-[3.125rem]" />
              ) : (
                <Button
                  type="button"
                  variant={index === 0 ? "outline" : "primary"}
                  disabled={isSkeleton || (index === 1 && !canLockInSelection)}
                  className={
                    index === 0
                      ? "capitalize h-11 sm:h-[2.875rem] md:h-12 lg:h-14 rounded-[3.125rem] text-xs sm:text-[0.825rem] lg:text-sm font-semibold leading-3 sm:leading-[1.05rem]"
                      : "capitalize h-11 sm:h-[2.875rem] md:h-12 lg:h-14 rounded-[3.125rem] text-xs sm:text-[0.825rem] lg:text-sm font-semibold leading-3 sm:leading-[1.05rem]"
                  }
                  onClick={
                    index === 0
                      ? goKeepIncludedAllocation
                      : goLockInDataAndFrequencyUpsell
                  }
                >
                  {/* hover:bg-[#FAFAFA] border-[#EBEBEB] text-[#999999] */}
                  {index === 1 ? (
                    <img
                      src="/assets/arrow-right-white-thick.webp"
                      alt=""
                      className="animate-nudge-right w-[1.0938rem] h-[0.7188rem] sm:w-[1.2031rem] sm:h-[0.7906rem] lg:w-[1.3125rem] lg:h-[0.8625rem] mr-2 sm:mr-2.5 lg:mr-3"
                    />
                  ) : null}
                  {index === 0 ? "Keep Current Plan" : "Lock In Savings"}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
